import { useId, type CSSProperties, type ReactNode } from 'react';
import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/**
 * CWY form controls — Input, Select, Textarea, and the Field wrapper that gives
 * them a label.
 *
 * These exist because the app had thirteen different hand-rolled control styles:
 * radii ranging over `rounded-lg`/`rounded-xl`/`rounded-card`, padding from
 * `px-1.5 py-1` to `px-4 py-3`, borders split between `border-line` and the
 * pre-brand `border-slate-200`, and backgrounds split between paper and white.
 * Two fields on the same card could differ in height.
 *
 * Sizing comes from the `--control-*` tokens, which Button reads too — that is
 * what makes a button line up with the field beside it.
 */
export type ControlSize = 'sm' | 'md';

function controlStyle(size: ControlSize, invalid: boolean): CSSProperties {
  return {
    width: '100%',
    height: size === 'sm' ? 'var(--control-h-sm)' : 'var(--control-h-md)',
    padding: `0 ${size === 'sm' ? 'var(--control-px-sm)' : 'var(--control-px-md)'}`,
    fontFamily: 'var(--font-sans)',
    fontSize: size === 'sm' ? 'var(--control-text-sm)' : 'var(--control-text-md)',
    color: 'var(--text-body)',
    background: 'var(--surface-card)',
    border: `1px solid ${invalid ? 'var(--danger)' : 'var(--border-default)'}`,
    borderRadius: 'var(--control-radius)',
    outline: 'none',
    transition: 'border-color var(--dur-fast) var(--ease-standard)',
  };
}

/* Focus and disabled are pseudo-states, which inline styles cannot express.
   Data attributes let one stylesheet rule in globals.css cover every control. */
const CONTROL_ATTR = { 'data-control': '' } as const;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  controlSize?: ControlSize;
  invalid?: boolean;
}

export function Input({ controlSize = 'md', invalid = false, style, ...rest }: InputProps) {
  return <input {...CONTROL_ATTR} style={{ ...controlStyle(controlSize, invalid), ...style }} {...rest} />;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  controlSize?: ControlSize;
  invalid?: boolean;
}

export function Select({ controlSize = 'md', invalid = false, style, children, ...rest }: SelectProps) {
  return (
    <select
      {...CONTROL_ATTR}
      style={{
        ...controlStyle(controlSize, invalid),
        // Native selects reserve room for the chevron inconsistently across
        // browsers; the extra right padding keeps long option text off it.
        paddingRight: 'calc(var(--control-px-md) + 0.75rem)',
        ...style,
      }}
      {...rest}
    >
      {children}
    </select>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function Textarea({ invalid = false, rows = 4, style, ...rest }: TextareaProps) {
  const base = controlStyle('md', invalid);
  return (
    <textarea
      {...CONTROL_ATTR}
      rows={rows}
      style={{
        ...base,
        // A textarea grows with its content, so a fixed height would clip it.
        height: 'auto',
        minHeight: 'var(--control-h-md)',
        padding: `0.5rem var(--control-px-md)`,
        lineHeight: 1.5,
        resize: 'vertical',
        ...style,
      }}
      {...rest}
    />
  );
}

/**
 * Label + control + hint/error, with the label bound to the control by id.
 *
 * Pass a render function so the generated id reaches the control without every
 * caller having to invent one — an unlabelled field is the accessibility bug
 * this component exists to make hard to write.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  className = '',
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: (props: { id: string; invalid: boolean }) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-danger">
            *
          </span>
        )}
      </label>
      {children({ id, invalid: Boolean(error) })}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-ink-soft">{hint}</p>
      ) : null}
    </div>
  );
}
